import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftSelect from 'components/SoftSelect';
import axios from 'axios';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  },
});

const ModalCargarConsolidado = ({ show, handleClose, onUploadConsolidado }) => {
  const dropzoneRef = useRef(null);
  const dropzoneInstance = useRef(null);
  const [files, setFiles] = useState([]);
  const [transportType, setTransportType] = useState(null);
  const [isUploading, setIsUploading] = useState(false); // nuevo estado para el loading

  const transportOptions = [
    { value: 'aereo', label: 'Aéreo' },
    { value: 'maritimo', label: 'Marítimo' },
  ];

  // Handler para agregar archivo(s) al estado
  const handleFileAdded = (file) => {
    setFiles((prevFiles) => [
      ...prevFiles,
      {
        id: file.upload.uuid,
        name: file.name,
        raw: file,
      },
    ]);
  };

  // Handler para eliminar archivo(s) del estado
  const handleFileRemoved = (file) => {
    setFiles((prevFiles) =>
      prevFiles.filter((f) => f.id !== file.upload.uuid)
    );
  };

  // Inicializa Dropzone solo cuando el modal se muestra
  useEffect(() => {
    if (show && dropzoneRef.current) {
      const dz = new Dropzone(dropzoneRef.current, {
        url: '/file-upload', // URL de ejemplo (no se usa porque autoProcessQueue está deshabilitado)
        autoProcessQueue: false,
        addRemoveLinks: true,
        acceptedFiles: '.xls,.xlsx,.xslb,.csv',
        dictDefaultMessage: 'Arrastra los archivos aquí o haz clic para seleccionarlos',
        dictRemoveFile: 'Quitar archivo',
        dictCancelUpload: 'Cancelar',
        dictFileTooBig: 'El archivo es demasiado grande ({{filesize}}MB). Máximo: {{maxFilesize}}MB.',
        dictInvalidFileType: 'Solo se permiten archivos .xls, .xlsx, .xslb o .csv.',
      });
      dropzoneInstance.current = dz;
      dz.on('addedfile', handleFileAdded);
      dz.on('removedfile', handleFileRemoved);

      return () => {
        dz.destroy();
        dropzoneInstance.current = null;
      };
    }
  }, [show]);

  // Limpia el estado cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setFiles([]);
      setTransportType(null);
      setIsUploading(false);
    }
  }, [show]);

  // Función para cargar el consolidado
  const handleUpload = async () => {
    if (files.length === 0) {
      Toast.fire({
        icon: "warning",
        title: "No se ha seleccionado ningún archivo",
      });
      return;
    }
    if (!transportType) {
      Toast.fire({
        icon: "warning",
        title: "Seleccione el tipo de transporte",
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      // Se envía solo el primer archivo seleccionado
      formData.append("file", files[0].raw);
      formData.append("tipo", transportType === 'aereo' ? '1' : '2');

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/finance/consolidated/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Toast.fire({
        icon: "success",
        title: "Consolidado cargado con éxito",
      });

      onUploadConsolidado(response.data);

      if (dropzoneInstance.current) {
        dropzoneInstance.current.removeAllFiles();
      }
      setFiles([]);
      handleClose();
    } catch (error) {
      console.error("Error al cargar consolidado:", error.response || error);
      Toast.fire({
        icon: "error",
        title: "Error al cargar consolidado",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="md"
      centered
      backdrop="static"
      className="modal-container small"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Cargar Consolidado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoftBox mt={2}>
          <div
            ref={dropzoneRef}
            className="dropzone"
            style={{
              padding: '20px',
              border: '2px dashed #ddd',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <SoftTypography variant="body2" color="text">
              Arrastra y suelta el archivo o haz clic para seleccionar.
            </SoftTypography>
          </div>

          {files.length > 0 && (
            <SoftBox mt={2}>
              {files.map((file) => (
                <SoftTypography key={file.id} variant="caption" color="text">
                  {file.name}
                </SoftTypography>
              ))}
            </SoftBox>
          )}

          <SoftBox mt={3}>
            <SoftTypography variant="caption" fontWeight="bold" color="text">
              Tipo de Transporte
            </SoftTypography>
            <SoftSelect
              options={transportOptions}
              value={transportOptions.find((option) => option.value === transportType)}
              onChange={(selected) => setTransportType(selected.value)}
              placeholder="Selecciona una opción..."
              isClearable={false}
            />
          </SoftBox>
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton 
          variant="gradient" 
          color="success" 
          onClick={handleUpload}
          disabled={isUploading}  // se bloquea mientras se sube el archivo
        >
          {isUploading ? "Cargando archivo..." : "Cargar Consolidado"}
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
};

ModalCargarConsolidado.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onUploadConsolidado: PropTypes.func.isRequired,
};

export default ModalCargarConsolidado;
